import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { BuyingGroupMode } from "./use-account-activities";

export interface MilestoneAnalysisOtherCitation {
  id: string;
  field: string;
  detail: string;
  reason: string;
  inferred_from_id: string | null;
}

export interface MilestoneAnalysisTouchpoint {
  id: string;
  date: string;
  type: string;
  detail: string;
  reason: string;
  inferred_from_id: string | null;
  channel: string;
  activity: string;
  record_type: string;
  source_record_id: string;
  source_record_type: string;
}

export interface MilestoneAnalysisOpinion {
  opinion: string;
  assist_touchpoint_id: string;
  primary_touchpoint_id: string;
}

export interface MilestoneAnalysis {
  analysis_id: string;
  opportunity_id: string;
  opportunity_name: string;
  analysis_type: string;
  created: string;
  last_updated: string;
  opportunity_created_date: string;
  is_approved: boolean;
  is_active_version: boolean;
  task_id?: string;
  summary?: string;
  opinion?: MilestoneAnalysisOpinion;
  other_citations?: MilestoneAnalysisOtherCitation[];
  touchpoint_citations: MilestoneAnalysisTouchpoint[];
  primary_touchpoint_channel?: string;
  assist_touchpoint_channel?: string;
  extra_instructions?: string;
}

// TaskGroup interface returned by the server
export interface TaskGroup {
  task_id: string;
  opportunity_id: string;
  opportunity_created_date: string;
  analysis_type: string;
  processing_status: "failed" | "processing" | "completed" | "pending";
  error_message: string | null;
  failure_retry_count: number;
  extra_instructions: string | null;
  analyses: MilestoneAnalysis[];
}

/**
 * Hook for fetching milestone analysis for opportunities related to an account
 */
export function useMilestoneAnalyses(
  accountId?: string,
  opportunityIds?: string[],
  buyingGroupMode: BuyingGroupMode = "default",
  enabled: boolean = true,
) {
  // Custom logic for enabling/disabling query based on the specified rules:
  // 1. Always check for accountId first to prevent unnecessary queries
  // 2. When the mode is custom and there are no opportunity IDs, return nothing
  const isQueryEnabled =
    enabled &&
    !!accountId &&
    !(
      buyingGroupMode === "custom" &&
      (!opportunityIds || opportunityIds.length === 0)
    );

  return useQuery<TaskGroup[]>({
    queryKey: [
      "milestone-analyses",
      accountId,
      opportunityIds,
      buyingGroupMode,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (accountId) {
        params.account_id = accountId;
      }
      // Rules 2 & 4: When mode is custom/other and there are opportunity IDs, pass them
      if (opportunityIds?.length) {
        params.opportunity_ids = opportunityIds.join(",");
      }
      // Always pass buying group mode to backend
      params.buying_group_mode = buyingGroupMode;

      const moments = await api.get<TaskGroup[]>(
        "datahub/milestone-analyses/",
        {
          params,
        },
      );
      return moments;
    },
    enabled: isQueryEnabled,
  });
}

interface UpdateTaskParams {
  task_id: string;
  processing_status?: string;
  extra_instructions?: string;
}

/**
 * Hook for updating a milestone analysis task
 */
export function useUpdateMilestoneAnalysisTask() {
  const queryClient = useQueryClient();

  return useMutation<
    { task_id: string; status: string },
    Error,
    UpdateTaskParams
  >({
    mutationFn: async (params: UpdateTaskParams) => {
      const response = await api.patch<{ task_id: string; status: string }>(
        `datahub/milestone-analyses/tasks/${params.task_id}/`,
        {
          processing_status: params.processing_status || "pending",
          extra_instructions: params.extra_instructions || "",
        },
      );
      return response;
    },
    onSuccess: (_, variables) => {
      if (variables.extra_instructions) {
        toast.success("Analysis regenerated successfully");
      } else {
        toast.success("Analysis request approved and added to queue");
      }
      queryClient.invalidateQueries({
        queryKey: ["milestone-analyses"],
      });

      // Also invalidate opportunities query for opportunity list view
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
      });
    },
    onError: (_, variables) => {
      if (variables?.extra_instructions) {
        toast.error("Failed to regenerate analysis");
      } else {
        toast.error("Failed to approve analysis");
      }
    },
  });
}

interface RetryParams {
  task_id: string;
}

/**
 * Hook for retrying a failed milestone analysis analysis
 */
export function useRetryMilestoneAnalysisAnalysis() {
  const queryClient = useQueryClient();

  return useMutation<{ task_id: string; status: string }, Error, RetryParams>({
    mutationFn: async (params: RetryParams) => {
      const response = await api.post<{ task_id: string; status: string }>(
        `datahub/milestone-analyses/tasks/${params.task_id}/retry/`,
        {},
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate all milestone analysis queries since we don't know which ones might be affected
      queryClient.invalidateQueries({
        queryKey: ["milestone-analyses"],
      });

      // Also invalidate opportunities query for opportunity list view
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
      });
    },
    onError: () => {
      toast.error("Failed to retry analysis");
    },
  });
}

interface UpdateStatusParams {
  analysisId: string;
  isActiveVersion?: boolean;
  isApproved?: boolean;
}

/**
 * Hook for updating a milestone analysis status (approval and active state)
 */
export function useUpdateMilestoneAnalysisStatus() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; analysis_id: string },
    Error,
    UpdateStatusParams
  >({
    mutationFn: async ({
      analysisId,
      isActiveVersion = false,
      isApproved = true,
    }: UpdateStatusParams) => {
      const response = await api.patch<{
        success: boolean;
        analysis_id: string;
      }>(`datahub/milestone-analyses/analysis-results/${analysisId}/`, {
        is_active_version: isActiveVersion,
        is_approved: isApproved,
      });
      return response;
    },
    onSuccess: (_, { analysisId, isActiveVersion, isApproved }) => {
      // Update cache directly to prevent UI delay
      queryClient.setQueriesData(
        { queryKey: ["milestone-analyses"] },
        (oldData: TaskGroup[] | undefined) => {
          if (!oldData) return oldData;

          // Handle array of task groups
          if (Array.isArray(oldData)) {
            return oldData.map((taskGroup) => {
              // Find the task group containing this analysis
              if (
                !taskGroup.analyses.some(
                  (a: MilestoneAnalysis) => a.analysis_id === analysisId,
                )
              ) {
                return taskGroup;
              }

              // Map through all analyses and update the matching one
              const updatedAnalyses = taskGroup.analyses.map(
                (analysis: MilestoneAnalysis) => {
                  if (analysis.analysis_id === analysisId) {
                    return {
                      ...analysis,
                      is_approved: isApproved,
                      is_active_version: isActiveVersion,
                    };
                  }

                  // If setting a new active version, ensure only one is active
                  if (isActiveVersion && analysis.is_active_version) {
                    return {
                      ...analysis,
                      is_active_version: false,
                    };
                  }

                  return analysis;
                },
              );

              return {
                ...taskGroup,
                analyses: updatedAnalyses,
              };
            });
          }

          return oldData;
        },
      );

      // Still invalidate the queries to eventually get fresh data
      queryClient.invalidateQueries({
        queryKey: ["milestone-analyses"],
      });
    },
  });
}

interface CreateTaskParams {
  opportunity_id: string;
  analysis_type: string;
}

/**
 * Hook for creating a new milestone analysis task
 */
export function useCreateMilestoneAnalysisTask() {
  const queryClient = useQueryClient();

  return useMutation<
    { task_id: string; status: string },
    Error,
    CreateTaskParams
  >({
    mutationFn: async (params: CreateTaskParams) => {
      const response = await api.post<{
        task_id: string;
        status: string;
      }>("datahub/milestone-analyses/tasks/create/", {
        opportunity_id: params.opportunity_id,
        analysis_type: params.analysis_type,
      });
      return response;
    },
    onSuccess: () => {
      toast.success("Analysis requested successfully");
      queryClient.invalidateQueries({ queryKey: ["milestone-analyses"] });

      // Also invalidate opportunities query for opportunity list view
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to request analysis";
      // Type guard to check if error is an Error object with a message
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      } else if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
      ) {
        errorMessage = (error as { message: string }).message;
      }
      toast.error(errorMessage, { duration: 5000 });
    },
  });
}
